// ─── Types ───────────────────────────────────────────────────────────────────
// NOTE: This interface intentionally shadows the browser's native `FormData`.
// It is used throughout this feature as the request form's data shape.

export interface FormData {
  jobTitle: string;
  jobPostingUrl: string;
  experienceLevel: string;
  startDate: Date | null;
  location: string;
  skills: string[];
  tools: string[];
  languages: string[];
  workingPace: string;
  collaboration: string;
  problemType: string;
  directionLevel: string;
  communicationStyle: string;
  additionalNotes: string;
  idealCandidateDescription: string;
}

export const INITIAL_FORM: FormData = {
  jobTitle: "",
  jobPostingUrl: "",
  experienceLevel: "early-career", // Early career selected by default per Figma
  startDate: null,
  location: "",
  skills: [],
  tools: [],
  languages: [],
  workingPace: "",
  collaboration: "",
  problemType: "",
  directionLevel: "",
  communicationStyle: "",
  additionalNotes: "",
  idealCandidateDescription: "",
};

export const STEP_LABELS = ["Basics", "Capabilities", "Work personality", "Good to know", "Review"];

export const INTRO_STEPS = [
  {
    title: "Tell us what you need",
    description: "A few quick questions about the role and your preferences.",
  },
  {
    title: "We find the right fit",
    description: "Our team hand-picks candidates that match your requirements.",
  },
  {
    title: "You interview & hire",
    description: "Meet who you like and bring the right person on board.",
  },
];

export const EXPERIENCE_LEVELS = [
  { value: "early-career", label: "Early career" },
  { value: "mid-level", label: "Mid level" },
  { value: "seasoned", label: "Seasoned" },
];

export interface PersonalityOption {
  value: string;
  label: string;
  description: string;
}

export interface PersonalityQuestion {
  key: keyof Pick<FormData, "workingPace" | "collaboration" | "problemType" | "directionLevel" | "communicationStyle">;
  category: string;
  question: string;
  options: PersonalityOption[];
}

export const PERSONALITY_QUESTIONS: PersonalityQuestion[] = [
  {
    key: "workingPace",
    category: "WORKING PACE",
    question: "What kind of environment will they be in?",
    options: [
      { value: "fast-paced", label: "Fast-paced", description: "High volume, shifting priorities" },
      { value: "deep-focus", label: "Deep-focus", description: "Fewer tasks, more depth and attention" },
      { value: "no-preference", label: "No preferences", description: "Either style works for this role" },
    ],
  },
  {
    key: "collaboration",
    category: "COLLABORATION",
    question: "How will they work with others?",
    options: [
      { value: "independent", label: "Independent", description: "Minimal supervision, owns their decisions" },
      { value: "collaborative", label: "Collaborative", description: "Works closely, supports shared goals" },
      { value: "no-preference", label: "No preferences", description: "Flexible on collaboration style" },
    ],
  },
  {
    key: "problemType",
    category: "PROBLEM TYPE",
    question: "What kind of challenges will they tackle?",
    options: [
      { value: "defined", label: "Defined", description: "Clear objectives and measurable outcomes" },
      { value: "open-ended", label: "Open-ended", description: "Ambiguous situations, requires judgment" },
      { value: "no-preference", label: "No preferences", description: "A mix of both is fine" },
    ],
  },
  {
    key: "directionLevel",
    category: "DIRECTION LEVEL",
    question: "How much guidance will you provide?",
    options: [
      { value: "light-touch", label: "Light touch", description: "They will figure out the how" },
      { value: "hands-on", label: "Hands-on", description: "You define the scope and direction" },
      { value: "no-preference", label: "No preferences", description: "Depends on the task" },
    ],
  },
  {
    key: "communicationStyle",
    category: "COMMUNICATION STYLE",
    question: "How will they interact with clients?",
    options: [
      {
        value: "relationship-first",
        label: "Relationship-first",
        description: "Builds rapport, invests in connections",
      },
      { value: "task-first", label: "Task-first", description: "Friendly, but focused on outcomes" },
      { value: "not-applicable", label: "Not applicable", description: "No client interaction necessary" },
    ],
  },
];

export const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "4px",
    fontFamily: "Inter, sans-serif",
    fontSize: 16,
    padding: "0px 12px",
    "& fieldset": { borderColor: "#DFDFDF" },
    "&:hover fieldset": { borderColor: "#DFDFDF" },
    "&.Mui-focused fieldset": { borderColor: "#8139D0" },
  },
  "& .MuiInputBase-input": {
    fontFamily: "Inter, sans-serif",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "24px",
    color: "#000000",
    padding: 0,
    "&::placeholder": { color: "#999999", opacity: 1 },
  },
};

export const TODAY = new Date();

// Returns `date` advanced by exactly `n` business days (skipping Sat/Sun).
// Example: April 3 (Fri) + 10 business days → April 17 (Fri).
export function addBusinessDays(date: Date, n: number): Date {
  const result = new Date(date);
  let counted = 0;
  while (counted < n) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) counted += 1; // skip Sunday (0) and Saturday (6)
  }
  return result;
}

// Earliest selectable start date: 10 business days from today
export const MIN_START_DATE = addBusinessDays(TODAY, 10);

export const MAX_CHARS = 500;
